import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useView } from '../context/ViewContext';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const DashboardCharts = () => {
    const { isOwnerView, user } = useView();
    const [data, setData] = useState({
        status_counts: [],
        daily_orders: [],
        product_popularity: [],
        ratings_distribution: [],
        heatmap_data: [],
        inventory_alerts: [],
        stock_consumption: []
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                let res;

                if (isOwnerView) {
                    // Fetch ALL data for owner view
                    res = await axios.get(`https://rotica.in/get_owner_analytics.php`);
                } else {
                    // Fetch data for specific user
                    if (!user.id || !user.role) return;
                    res = await axios.get(`https://rotica.in/get_analytics_data.php?user_id=${user.id}&role=${user.role}`);
                }

                if (res.data.success) {
                    setData(res.data);
                }
            } catch (error) {
                console.error("Error fetching analytics:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [isOwnerView, user]);

    // 1. Status Chart (Doughnut)
    const statusChartData = {
        labels: data.status_counts.map(d => d.status),
        datasets: [{
            data: data.status_counts.map(d => d.count),
            backgroundColor: ['#3498db', '#2ecc71', '#e67e22', '#e74c3c', '#9b59b6', '#f1c40f'],
            borderWidth: 0
        }]
    };

    // 2. Daily Growth (Line)
    const growthChartData = {
        labels: data.daily_orders.map(d => d.date),
        datasets: [{
            label: 'Orders',
            data: data.daily_orders.map(d => d.count),
            borderColor: '#0071e3',
            backgroundColor: 'rgba(0, 113, 227, 0.1)',
            fill: true,
            tension: 0.4
        }]
    };

    // 3. Product Popularity (Bar)
    const popularityChartData = {
        labels: data.product_popularity.map(p => p.base_name),
        datasets: [{
            label: 'Units Sold',
            data: data.product_popularity.map(p => p.total_sold),
            backgroundColor: '#8e44ad',
            borderRadius: 6
        }]
    };

    // 4. Ratings (Bar)
    const ratingsChartData = {
        labels: ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'],
        datasets: [{
            label: 'Count',
            data: [1, 2, 3, 4, 5].map(r => {
                const found = data.ratings_distribution.find(d => (parseInt(d.rating) || 0) === r);
                return found ? found.count : 0;
            }),
            backgroundColor: '#f1c40f',
            borderRadius: 6
        }]
    };

    // 7. Stock Consumption (Line)
    const stockChartData = {
        labels: data.stock_consumption.map(d => d.date),
        datasets: [{
            label: 'Total Units Consumed',
            data: data.stock_consumption.map(d => d.total_units),
            borderColor: '#e74c3c',
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
            fill: true,
            tension: 0.4
        }]
    };

    // Calculate Summary Metrics
    const totalOrders = data.status_counts.reduce((acc, curr) => acc + (parseInt(curr.count) || 0), 0);
    const activeOrders = data.status_counts
        .filter(d => ['Pending', 'Accepted', 'Assigned', 'Out for Delivery'].includes(d.status))
        .reduce((acc, curr) => acc + (parseInt(curr.count) || 0), 0);
    const totalUnitsSold = data.stock_consumption.reduce((acc, curr) => acc + (parseInt(curr.total_units) || 0), 0);
    const lowStockCount = data.inventory_alerts.length;

    if (loading) return (
        <div className="analytics-loading">
            <div className="spinner"></div>
            <p>Gathering Insights...</p>
        </div>
    );

    return (
        <div className="analytics-page">
            <header className="page-header">
                <div>
                    <h1>{isOwnerView ? 'Product Facts (All Entities)' : 'Product Facts'}</h1>
                    <p className="subtitle">Overview of orders, inventory, and performance</p>
                </div>
                <div className="header-date">
                    <span>Last 30 Days</span>
                </div>
            </header>

            {/* Summary Metrics Row */}
            <div className="metrics-grid">
                <div className="metric-card">
                    <div className="metric-icon blue">📦</div>
                    <div className="metric-info">
                        <h3>Total Orders</h3>
                        <p>{totalOrders}</p>
                    </div>
                </div>
                <div className="metric-card">
                    <div className="metric-icon green">🚀</div>
                    <div className="metric-info">
                        <h3>Active Now</h3>
                        <p>{activeOrders}</p>
                    </div>
                </div>
                <div className="metric-card">
                    <div className="metric-icon purple">🛍️</div>
                    <div className="metric-info">
                        <h3>Units Sold (30d)</h3>
                        <p>{totalUnitsSold}</p>
                    </div>
                </div>
                <div className="metric-card">
                    <div className="metric-icon orange">⚠️</div>
                    <div className="metric-info">
                        <h3>Stagnant Items</h3>
                        <p>{lowStockCount}</p>
                    </div>
                </div>
            </div>

            {/* Inventory Alerts */}
            {data.inventory_alerts.length > 0 && (
                <div className="alert-banner">
                    <div className="alert-icon">⚠️</div>
                    <div className="alert-content">
                        <h3>Stagnant Inventory Attention Needed</h3>
                        <p>{data.inventory_alerts.length} products have not been updated in over 6 days.</p>
                        <div className="alert-tags">
                            {data.inventory_alerts.slice(0, 5).map(item => (
                                <span key={item.id} className="alert-tag">
                                    {item.entity_name ? `${item.entity_name}: ` : ''}{item.base_name}: {item.age_days}d
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="charts-grid">
                {/* Row 1: Status & Popularity */}
                <div className="chart-card">
                    <h3>Order Status Distribution</h3>
                    <div className="chart-wrapper-doughnut">
                        <Doughnut data={statusChartData} options={{ responsive: true, plugins: { legend: { position: 'right' } } }} />
                    </div>
                </div>
                <div className="chart-card">
                    <h3>Product Popularity</h3>
                    <div className="chart-wrapper">
                        <Bar data={popularityChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                    </div>
                </div>

                {/* Row 2: Heatmap & Ratings */}
                <div className="chart-card">
                    <h3>Delivery Zone Heatmap</h3>
                    <div className="map-wrapper">
                        <MapContainer center={[20.5937, 78.9629]} zoom={5} style={{ height: '100%', width: '100%', borderRadius: '12px' }}>
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; OpenStreetMap contributors'
                            />
                            {data.heatmap_data.map((pos, idx) => (
                                <CircleMarker
                                    key={idx}
                                    center={[pos.lat, pos.lng]}
                                    radius={12}
                                    fillColor="#e74c3c"
                                    color="#c0392b"
                                    weight={1}
                                    opacity={1}
                                    fillOpacity={0.6}
                                >
                                    <Popup>Order Location</Popup>
                                </CircleMarker>
                            ))}
                        </MapContainer>
                    </div>
                </div>
                <div className="chart-card">
                    <h3>User Ratings</h3>
                    <div className="chart-wrapper">
                        <Bar data={ratingsChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                    </div>
                </div>

                {/* Row 3: Daily Growth (Wide) */}
                <div className="chart-card full-width">
                    <h3>Daily Order Growth (Last 30 Days)</h3>
                    <div className="chart-wrapper">
                        <Line data={growthChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                    </div>
                </div>

                {/* Row 4: Stock Consumption (Wide) */}
                <div className="chart-card full-width">
                    <h3>Stock Consumption Trend (Units/Day)</h3>
                    <div className="chart-wrapper">
                        <Line data={stockChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardCharts;
